import React, { useRef, useCallback, memo, Activity } from "react";
import { Accordion, Division, Flex, Heading, TabPanel, Tabs } from "@ir/ui";
import { useConfirm, useUserPermissions, useWorkspace } from "@/hooks";
import { CategoryKey, Deduction, DeductionTypeCategoryKey } from "@/core/entities/deduction";
import { useDeductionDrawer } from "@/presentation/deduction/hooks";
import { DeductionActivities } from "@/presentation/deduction/components/DeductionDrawer/DeductionActivity";
import { HasAnyPermission } from "@/presentation/workspace";
import { WorkspacePermissions } from "@/core/permissions/Workspace.permissions";
import {
  DeductionChargeBreakdown,
  DeductionDocuments,
  DeductionDocumentViewer,
  DeductionInformation,
  DeductionIntelligence,
  DeductionTPMAutoMatch,
} from "@/presentation/deduction/components/DeductionDrawer/DeductionInformation";
import { TabType } from "@/store/deduction/drawer";
import DeductionTabList from "./DeductionDrawerTabs/TabList";
import DeductionIncompleteWrapper from "./DeductionIncomplete/DeductionIncompleteWrapper";
import DeductionFetchingError from "./DeductionFetchingError";
import DeductionNotfound from "./DeductionNotfound";
import useDeductionIncomplete from "./DeductionIncomplete/hook/useDeductionIncomplete";
import InternalConversation from "../../internal-conversations/InternalConversation";

const MemoizedDeductionActivities = memo(DeductionActivities);
const MemoizedDeductionDocumentViewer = memo(DeductionDocumentViewer);

const DeductionContent = memo(() => {
  const {
    deductions,
    deductionsError,
    deductionsCount,
    isDeductionsLoading,
    isDocumentViewerOpen,
    viewerDocuments,
    drawerInfo,
    hasAnyUnsavedChanges,
    setHasAnyUnsavedChanges,
    selectedTab,
    setSelectedTab,
    currentExpandedDeductionId,
    setCurrentExpandedDeductionId,
    isDrawerModal,
  } = useDeductionDrawer();
  const { isLoadingDeductionStatus } = useDeductionIncomplete();
  const { settings: workspaceSettings } = useWorkspace();
  const confirm = useConfirm();
  const { hasAnyPermission } = useUserPermissions();
  const contentRef = useRef<HTMLDivElement>(null);
  const showUnSavedConfirmation = useCallback(async (): Promise<boolean> => {
    if (hasAnyUnsavedChanges) {
      const confirmResult = await confirm({
        title: "Unsaved Changes",
        message: "You have unsaved updates. Leave without saving?",
        confirmText: "Discard",
        cancelText: "Go Back",
      });
      if (confirmResult) {
        setHasAnyUnsavedChanges(false);
      }
      return confirmResult;
    }
    return true;
  }, [hasAnyUnsavedChanges, confirm, setHasAnyUnsavedChanges]);

  const formatDeductionAmount = useCallback(
    (deduction: Deduction) => {
      if (workspaceSettings?.displayNumberFormat && deduction?.amount) {
        return "$" + deduction?.amountFormatted(workspaceSettings?.displayNumberFormat);
      }
      return deduction?.amount;
    },
    [workspaceSettings]
  );

  const renderDeductionContent = (deduction: Deduction) => {
    return (
      <>
        <DeductionIntelligence deduction={deduction} />
        <DeductionInformation deduction={deduction} />
        <Activity
          mode={
            !isDeductionsLoading &&
            deduction?.deductionTypeCategory?.deductionTypeCategoryKey === DeductionTypeCategoryKey.TRADE
              ? "visible"
              : "hidden"
          }
        >
          <DeductionTPMAutoMatch deduction={deduction} />
        </Activity>
        <DeductionDocuments deduction={deduction} />
        <DeductionChargeBreakdown deduction={deduction} />
      </>
    );
  };

  const renderDeductions = () => {
    if (deductionsCount > 1) {
      return (
        <Accordion
          key={currentExpandedDeductionId ?? "none"}
          defaultExpandedKeys={currentExpandedDeductionId ? [currentExpandedDeductionId.toString()] : undefined}
          items={deductions.map(deduction => ({
            key: deduction.id.toString(),
            title: getAccordionTitle(deduction),
            content: renderDeductionContent(deduction),
          }))}
          onExpandedChange={keys => {
            const expandedKey = keys[0];
            if (expandedKey) {
              setCurrentExpandedDeductionId(Number(expandedKey));
            }
          }}
        />
      );
    }
    return renderDeductionContent(deductions[0]);
  };

  const getAccordionTitle = (deduction: Deduction) => {
    return (
      <Flex align="center" justifyContent="space-between" className="flex-1">
        <Heading level={6} className="text-grey-700 font-bold m-0 text-sm">
          {deduction.splitHeaderText}
        </Heading>
        <Division
          className="text-grey-600 font-semibold d-inline-flex flex-align-center flex-justify-end"
          style={{ minWidth: "85px" }}
        >
          {formatDeductionAmount(deduction)}
        </Division>
      </Flex>
    );
  };

  const renderDeductionIncomplete = () => {
    if (deductionsError) return <DeductionFetchingError deductionsError={deductionsError} />;

    const hasIncompletePermissions = hasAnyPermission([
      WorkspacePermissions.DEDUCTION_INCOMPLETE_VIEW,
      WorkspacePermissions.DEDUCTION_INCOMPLETE_RESOLVE_BACKUP_REQUEST,
      WorkspacePermissions.DEDUCTION_INCOMPLETE_RESOLVE_BACKUP_UPLOAD,
      WorkspacePermissions.DEDUCTION_INCOMPLETE_RESOLVE_BACKUP_MANUAL,
    ]);

    const isNoDeductionsState = !isDeductionsLoading && !deductions?.length && !isLoadingDeductionStatus;

    if (isNoDeductionsState) {
      return hasIncompletePermissions ? <DeductionIncompleteWrapper /> : <DeductionNotfound />;
    }

    return renderDeductions();
  };

  const renderConversationsTabContent = () => {
    if (deductionsCount > 1) {
      return (
        <Accordion
          key={currentExpandedDeductionId ?? "none"}
          defaultExpandedKeys={currentExpandedDeductionId ? [currentExpandedDeductionId.toString()] : undefined}
          items={deductions.map(deduction => ({
            key: deduction.id.toString(),
            className: `conversation-accordion-item ${currentExpandedDeductionId === deduction.id && "conversation-accordion-item-expanded"}`,
            title: (
              <Flex align="center" justifyContent="space-between" className="flex-1">
                <Heading level={6} className="text-grey-700 font-bold m-0 text-sm">
                  {deduction.deductionSubType}
                </Heading>
                <Division
                  className="text-grey-600 font-semibold d-inline-flex flex-align-center flex-justify-end"
                  style={{ minWidth: "85px" }}
                >
                  {formatDeductionAmount(deduction)}
                </Division>
              </Flex>
            ),
            content: (
              <Activity mode={currentExpandedDeductionId === deduction.id ? "visible" : "hidden"}>
                <InternalConversation referenceId={deduction.id} categoryKey={CategoryKey.DEDUCTION} />
              </Activity>
            ),
          }))}
          onExpandedChange={keys => {
            const expandedKey = keys[0];
            if (expandedKey) {
              setCurrentExpandedDeductionId(Number(expandedKey));
            }
          }}
        />
      );
    }
    return <InternalConversation referenceId={deductions[0]?.id} categoryKey={CategoryKey.DEDUCTION} />;
  };

  return (
    <Flex direction="row" align="stretch" justifyContent="end">
      <Tabs
        orientation="horizontal"
        aria-label="Deductions list"
        style={{ height: "100%", width: isDocumentViewerOpen && isDrawerModal ? "50%" : "100%" }}
        keyboardActivation="manual"
        selectedKey={selectedTab ?? TabType.INFORMATION}
        onSelectionChange={async key => {
          if (key !== TabType.INFORMATION) {
            const confirm = await showUnSavedConfirmation();
            if (!confirm) {
              return;
            }
          }
          setSelectedTab(key as TabType);
        }}
      >
        <Division className="bg-white border-bottom-1 border-color-grey-50 px-4">
          <Flex justifyContent="space-between" align="center" style={{ marginBottom: "-1px" }}>
            <DeductionTabList />
          </Flex>
        </Division>
        <Division ref={contentRef} className="flex flex-1 flex-align-start">
          <TabPanel
            id={TabType.INFORMATION}
            role="tabpanel"
            aria-label={TabType.INFORMATION}
            className="flex-1 w-100 p-4"
            style={{ height: "calc(100vh - 115px)", overflow: "auto" }}
          >
            <Division className="Information-Drawer-Content w-100">{renderDeductionIncomplete()}</Division>
          </TabPanel>
          <HasAnyPermission requiredPermissions={[WorkspacePermissions.DEDUCTION_ACTIVITY_VIEW]}>
            <TabPanel
              id={TabType.ACTIVITY}
              role="tabpanel"
              aria-label={TabType.ACTIVITY}
              className="flex-1 w-100 p-4"
              style={{ height: "calc(100vh - 115px)", overflow: "auto" }}
            >
              <Division className="Activity-Drawer-Content w-100">
                {deductionsCount > 1 ? (
                  <Accordion
                    key={currentExpandedDeductionId ?? "none"}
                    defaultExpandedKeys={
                      currentExpandedDeductionId ? [currentExpandedDeductionId.toString()] : undefined
                    }
                    items={deductions.map(deduction => ({
                      key: deduction.id.toString(),
                      title: getAccordionTitle(deduction),
                      content: (
                        <MemoizedDeductionActivities
                          scrollRef={contentRef as React.RefObject<HTMLDivElement>}
                          key={deduction?.id}
                          deduction={deduction}
                        />
                      ),
                    }))}
                    onExpandedChange={keys => {
                      const expandedKey = keys[0];
                      if (expandedKey) {
                        setCurrentExpandedDeductionId(Number(expandedKey));
                      }
                    }}
                  />
                ) : (
                  <MemoizedDeductionActivities
                    scrollRef={contentRef as React.RefObject<HTMLDivElement>}
                    key={deductions[0]?.id}
                    deduction={deductions[0]}
                  />
                )}
              </Division>
            </TabPanel>
          </HasAnyPermission>
          <HasAnyPermission requiredPermissions={[WorkspacePermissions.CONVERSATIONS_VIEW]}>
            <TabPanel
              id={TabType.CONVERSATIONS}
              role="tabpanel"
              aria-label={TabType.CONVERSATIONS}
              className="flex-1 w-100 p-4 internal-conversation-tab-panel"
              style={{ height: "calc(100vh - 115px)", overflow: deductionsCount > 1 ? "auto" : "hidden" }}
            >
              {drawerInfo?.isDeductionIncomplete ? (
                <InternalConversation
                  referenceId={drawerInfo?.remittanceItemId ?? 0}
                  categoryKey={CategoryKey.REMITTANCE_ITEM}
                />
              ) : (
                <Division className="Activity-Drawer-Content w-100">{renderConversationsTabContent()}</Division>
              )}
            </TabPanel>
          </HasAnyPermission>
        </Division>
      </Tabs>

      {isDocumentViewerOpen && viewerDocuments && drawerInfo?.invoiceNumber && (
        <MemoizedDeductionDocumentViewer
          className="open-document-viewer"
          documents={viewerDocuments}
          invoiceNumber={drawerInfo.invoiceNumber}
        />
      )}
    </Flex>
  );
});

DeductionContent.displayName = "DeductionContent";

export default DeductionContent;
